
// Packages
import { defineComponent } from 'vue';
import { notNumber, required, requiredIf } from '@white-label-helper/vuelidate';
import { mapActions, mapState } from 'pinia';

// Helpers
import { getFeatureFlag, isGtm2025LoungePassAndTravelInsuranceCombinedEnabled } from '@white-label-plugin/launch-darkly';
import { FEATURE_FLAGS, PRODUCT_TYPES } from '@white-label-configuration/constants';
import { useManageBookingStore } from '@white-label-store/manage-booking';
import { isLoungePass } from '@white-label-helper/is-lounge-pass';
import { updateBookingDetails } from '@white-label-helper/api-manage-booking';
import { useOpenModal } from '@white-label-helper/shared-composables';
import { datadogRum } from '@datadog/browser-rum';

// Components
import UpdateAdditionalTravellersCombinedProductsList from '../update-additional-travellers-combined-products-list/update-additional-travellers-combined-products-list.vue';
import AdditionalTravellersCombinedProductSummary from '../additional-travellers-combined-product-summary/additional-travellers-combined-product-summary.vue';
import AdditionalTravellersCombinedProductsModal from '../additional-travellers-combined-products-modal/additional-travellers-combined-products-modal.vue';
import AdditionalTravellersCombinedProductForm from '../additional-travellers-combined-product-form/additional-travellers-combined-product-form.vue';

// Types
import type { LoungePassTraveller, TravelInsuranceTraveller } from 'ecommerce/feature-additional-travellers-combined-products';
import { type CustomerGroupLabels, getProductTypeCustomerGroupLabels } from '@white-label-helper/get-product-type-customer-group-labels';
import type { Traveller } from '@white-label-types/travel-insurance';
import type { ManageBookingOriginalOrderItem } from '@white-label-types/store';

import { NAMED_ROUTES, HTTP_ERROR_TYPES, BOOKING_STATUSES } from '@white-label-configuration/constants';

function formatLoungePassAdditionalTraveller(orderItemTraveller: Traveller): LoungePassTraveller {
  const customerGroupLabels: CustomerGroupLabels = getProductTypeCustomerGroupLabels(PRODUCT_TYPES.LOUNGES);
  const additionalTraveller: LoungePassTraveller = {
    firstName: orderItemTraveller.user.first_name || '',
    lastName: orderItemTraveller.user.last_name || '',
    ageGroup: orderItemTraveller.user.age_group || '',
    ageGroupLabel: customerGroupLabels[orderItemTraveller.user.age_group as keyof CustomerGroupLabels] || '',
  };

  return additionalTraveller;
}
function formatTravelInsuranceAdditionalTraveller(orderItemTraveller: Traveller): TravelInsuranceTraveller {
  const additionalTraveller: TravelInsuranceTraveller = {
    firstName: orderItemTraveller.user.first_name || '',
    lastName: orderItemTraveller.user.last_name || '',
    date_of_birth: orderItemTraveller.user.date_of_birth || ''
  };

  return additionalTraveller;
}

interface AdditionalTravellerDetails extends TravelInsuranceTraveller, LoungePassTraveller {
  traveller_id: string;
}

interface PayloadTravellerDetails {
  id: string;
  first_name: string;
  last_name: string;
}

export default defineComponent({
  name: 'UpdateAdditionalTravellersCombinedProducts',

  components: {
    UpdateAdditionalTravellersCombinedProductsList,
    AdditionalTravellersCombinedProductSummary,
    AdditionalTravellersCombinedProductsModal,
    AdditionalTravellersCombinedProductForm,
  },

  setup() {
    const { openModal } = useOpenModal();
    return {
      openModal,
    };
  },

  data() {
    return {
      travelInsuranceAdditionalTravellersDetails: [] as AdditionalTravellerDetails[],
      loungePassAdditionalTravellersDetails: [] as AdditionalTravellerDetails[],
      isAdditionalTravellersProductsListModalVisible: false,
      isLoading: false,
      initialState: {
        travelInsuranceAdditionalTravellersDetails: [] as AdditionalTravellerDetails[],
        loungePassAdditionalTravellersDetails: [] as AdditionalTravellerDetails[],
      },
    };
  },

  validations() {
    const self = this;
    const travellerNamesValidation = {
      required,
      notNumber,
    };

    const validations = {
      travelInsuranceAdditionalTravellersDetails: {
        $each: {
          firstName: travellerNamesValidation,
          lastName: travellerNamesValidation,
        },
      },
      loungePassAdditionalTravellersDetails: {
        $each: {
          firstName: {
            required: requiredIf(function checkIfRequired(traveller) {
              return self.isLoungePassAdditionalTravellersDetailsRequired || traveller.lastName.length;
            }),
            notNumber
          },
          lastName: {
            required: requiredIf(function checkIfRequired(traveller) {
              return self.isLoungePassAdditionalTravellersDetailsRequired || traveller.firstName.length;
            }),
            notNumber
          },
        },
      }
    };

    return validations;
  },

  computed: {
    ...mapState(useManageBookingStore, {
      manageBookingItems: 'readManageBookingItems',
      userInfo: 'readUserInfo',
      orderId: 'readOrderId',
      isUpdatingBookingDetails: 'isUpdatingBookingDetails',
    }),
    travelInsuranceOrderItem(): ManageBookingOriginalOrderItem {
      return this.manageBookingItems?.find(
        (item) => item.product_code === PRODUCT_TYPES.TRAVEL_INSURANCE
      );
    },
    loungePassOrderItem(): ManageBookingOriginalOrderItem {
      return this.manageBookingItems?.find(
        (item) => isLoungePass(item)
      );
    },

    isLoungePassAdditionalTravellersDetailsRequired(): boolean {
      if (!this.loungePassOrderItem) return false;
      return this.loungePassOrderItem.inventory_item.additional_fields
      && this.loungePassOrderItem.inventory_item.additional_fields['additional_passengers']
      && this.loungePassOrderItem.inventory_item.additional_fields['additional_passengers'].is_required === true;
    },
    validationMessages() {
      return {
        first_name: this.$t('UI.formErrors.firstname'),
        last_name: this.$t('UI.formErrors.lastname'),
      }
    },
    isOtherUpdateInProgress(): boolean {
      return this.isUpdatingBookingDetails || this.isLoading;
    },
    isEditDisabled() {
      let status;
      if (this.travelInsuranceOrderItem) {
        status = this.travelInsuranceOrderItem?.status?.toUpperCase();
      }
      if (this.loungePassOrderItem) {
        status = this.loungePassOrderItem?.status?.toUpperCase();
      }

      const hasChanges = JSON.stringify(this.travelInsuranceAdditionalTravellersDetails) !== JSON.stringify(this.initialState.travelInsuranceAdditionalTravellersDetails)
        || JSON.stringify(this.loungePassAdditionalTravellersDetails) !== JSON.stringify(this.initialState.loungePassAdditionalTravellersDetails);

      const statusBlocked = status !== BOOKING_STATUSES.CONFIRMED && status !== BOOKING_STATUSES.IN_PROGRESS;
      return statusBlocked || this.isOtherUpdateInProgress || (!hasChanges && this.$v.$dirty && this.isAdditionalTravellersProductsListModalVisible);
    },
  },
  methods: {
    ...mapActions(useManageBookingStore, {
      setIsUpdatingBookingDetails: 'setIsUpdatingBookingDetails',
    }),
    travelInsuranceAdditionalTravellers(): AdditionalTravellerDetails[] {
      if (!getFeatureFlag(FEATURE_FLAGS.EM_228_TRAVEL_INSURANCE, false)) return [];

      const additionalTravellers: Traveller[] = (this.travelInsuranceOrderItem?.travellers ?? []).filter(
        (traveller) => !traveller.is_lead
      );

      return additionalTravellers.map((traveller) => {
        return {
          ...traveller,
          ...formatTravelInsuranceAdditionalTraveller(traveller),
        }
      });
    },
    loungePassAdditionalTravellers(): AdditionalTravellerDetails[] {
      if (!isGtm2025LoungePassAndTravelInsuranceCombinedEnabled()) return [];
      const loungePassAdditionalTravellers: Traveller[] = (this.loungePassOrderItem?.travellers ?? []).filter(
        (traveller) => !traveller.is_lead
      );

      return loungePassAdditionalTravellers.map((traveller) => {
        return {
          ...traveller,
          ...formatLoungePassAdditionalTraveller(traveller),
        }
      });
    },
    handleUpdateTravellerDetails() {
      this.isAdditionalTravellersProductsListModalVisible = true;
    },
    handleCloseModal() {
      this.isAdditionalTravellersProductsListModalVisible = false
    },
    goToManageBooking() {
      this.$router.push(NAMED_ROUTES.manage_booking.default);
    },

    showBookingAlreadyCancelledModal() {
      this.openModal('GlobalModalError', {
        header: this.$t('shared.modals.errors.updatedBookingAlreadyCancelled.header') as string,
        body: this.$t('shared.modals.errors.updatedBookingAlreadyCancelled.body') as string,
        btnText: this.$t('shared.buttons.close') as string,
      }).then(() => this.goToManageBooking());
    },

    handleSubmitTravellerDetails() {
      this.$v.$touch();

      if (this.$v.$error) {
        return;
      }

      if (typeof this.orderId !== 'string') {
        throw new TypeError("Missing the order ID or its type isn't a string");
      }

      const travelInsuranceTravellersPayload = this.travelInsuranceAdditionalTravellersDetails.map((traveller) => ({
        id: traveller.traveller_id,
        first_name: traveller.firstName,
        last_name: traveller.lastName,
      }));

      const loungePassTravellersPayload = this.loungePassAdditionalTravellersDetails.map((traveller) => ({
        id: traveller.traveller_id,
        first_name: traveller.firstName,
        last_name: traveller.lastName,
      }));

      const additionalTravellersPayload: PayloadTravellerDetails[] = [];
      additionalTravellersPayload.push(...travelInsuranceTravellersPayload, ...loungePassTravellersPayload);

      const body = {
        order_reference: this.orderId,
        user: this.userInfo,
        additional_travellers: additionalTravellersPayload,
        update_all_confirmed_items: true,
      };

      this.isLoading = true;
      this.setIsUpdatingBookingDetails(true);

      updateBookingDetails(body)
        .then(() => {
          this.handleCloseModal();
          this.$router.push({
            path: NAMED_ROUTES.manage_booking.default,
            query: { personalDetailsSuccess: 'true' },
          });
        })
        .catch((error: any) => {
          const errorResponse = JSON.parse(error.message);
          if (
            errorResponse.body.error?.type ===
            HTTP_ERROR_TYPES.BOOKING_ALREADY_CANCELLED
          ) {
            this.handleCloseModal();
            this.showBookingAlreadyCancelledModal();
          }
          datadogRum.addError(
            new Error('Update Additional Traveller Details For Combined Products', { cause: error }),
            {
              label: 'updateAdditionalTravellerDetailsForCombinedProducts',
              requestHandler: 'updateBookingDetails',
            }
          );
        })
        .finally(() => {
          this.isLoading = false;
          this.setIsUpdatingBookingDetails(false);
        });
      },
  },
  mounted() {
    this.travelInsuranceAdditionalTravellersDetails = this.travelInsuranceAdditionalTravellers();
    this.loungePassAdditionalTravellersDetails = this.loungePassAdditionalTravellers();
    this.initialState.travelInsuranceAdditionalTravellersDetails = JSON.parse(JSON.stringify(this.travelInsuranceAdditionalTravellersDetails));
    this.initialState.loungePassAdditionalTravellersDetails = JSON.parse(JSON.stringify(this.loungePassAdditionalTravellersDetails));
  },
});
